/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// @mui material components
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import ReportsBarChart from 'examples/Charts/BarCharts/ReportsBarChart';
import ReportsLineChart from 'examples/Charts/LineCharts/ReportsLineChart';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import TimelineList from 'examples/Timeline/TimelineList';
import TimelineItem from 'examples/Timeline/TimelineItem';

import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

// Navbar
import Navbar from 'examples/Navbars/NavBar';

const initChart = {
  title: '',
  description: '',
  date: '',
  chart: {
    labels: [],
    datasets: { label: '', data: [] },
  },
};

function DashboardAdmin() {
  const mod = 'dashboard_admin';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [timelineLog, setTimelineLog] = useState([]);
  const [timelineUserWeb, setTimelineUserWeb] = useState([]);
  const [timelineUserApp, setTimelineUserApp] = useState([]);
  const [chartLogin, setChartLogin] = useState(initChart);
  const [chartDelete, setChartDelete] = useState(initChart);
  const [chartProduct, setChartProduct] = useState(initChart);
  const [loading, setLoading] = useState(true);
  const [stat, setStat] = useState({
    stat1: { count: '', color: 'white', amount: '', label: '' },
    stat2: { count: '', color: 'white', amount: '', label: '' },
    stat3: { count: '', color: 'white', amount: '', label: '' },
    stat4: { count: '', color: 'white', amount: '', label: '' },
    stat5: { count: '', color: 'white', amount: '', label: '' },
    stat6: { count: '', color: 'white', amount: '', label: '' },
  });

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setStat(result.stat);

        setChartLogin(result.login);
        setChartDelete(result.delete);
        setChartProduct(result.product);

        setTimelineLog(result.log);
        setTimelineUserWeb(result.userweb);
        setTimelineUserApp(result.userapp);
        setLoading(false);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderStat = () => {
    return (
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='dark'
                icon='settings_input_composite'
                title='ปริมาณสินค้า'
                count={loading ? '...' : stat.stat1.count}
                percentage={{
                  color: stat.stat1.color,
                  amount: loading ? '' : stat.stat1.amount,
                  label: loading ? 'loading...' : stat.stat1.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='warning'
                icon='dashboard_customize'
                title='สินค้าใหม่'
                count={loading ? '...' : stat.stat2.count}
                percentage={{
                  color: stat.stat2.color,
                  amount: loading ? '' : stat.stat2.amount,
                  label: loading ? 'loading...' : stat.stat2.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon='touch_app'
                title='การขอซื้อ'
                count={loading ? '...' : stat.stat3.count}
                percentage={{
                  color: stat.stat3.color,
                  amount: loading ? '' : stat.stat3.amount,
                  label: loading ? 'loading...' : stat.stat3.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='success'
                icon='monetization_on'
                title='การจัดซื้อ'
                count={loading ? '...' : stat.stat4.count}
                percentage={{
                  color: stat.stat4.color,
                  amount: loading ? '' : stat.stat4.amount,
                  label: loading ? 'loading...' : stat.stat4.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='primary'
                icon='add_business'
                title='การรับเข้า'
                count={loading ? '...' : stat.stat5.count}
                percentage={{
                  color: stat.stat5.color,
                  amount: loading ? '' : stat.stat5.amount,
                  label: loading ? 'loading...' : stat.stat5.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='error'
                icon='point_of_sale'
                title='การผลิต'
                count={loading ? '...' : stat.stat6.count}
                percentage={{
                  color: stat.stat6.color,
                  amount: loading ? '' : stat.stat6.amount,
                  label: loading ? 'loading...' : stat.stat6.label,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderChart = () => {
    if (loading) {
      return (
        <MDBox mt={2} mb={2}>
          <Grid container spacing={1}>
            {Array.from(Array(3)).map((_, index) => (
              <Grid item xs={12} md={6} lg={4} key={index.toString()}>
                <Card>
                  <CardContent sx={{ pt: 1.5, pb: 0, px: 1.5 }}>
                    <Skeleton variant='rectangular' height={300} />
                  </CardContent>
                  <CardHeader
                    title={<Skeleton animation='wave' height={20} width='40%' sx={{ mb: 1 }} />}
                    subheader={<Skeleton animation='wave' height={20} width='80%' />}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      );
    }

    return (
      <MDBox mt={6}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={3}>
              <ReportsBarChart
                color='info'
                title='การเข้าใช้ต่อวัน'
                description={chartLogin.description}
                date={chartLogin.date}
                chart={chartLogin.chart}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={3}>
              <ReportsLineChart
                color='dark'
                title='การเพิ่มสินค้าใหม่ต่อวัน'
                description={chartProduct.description}
                date={chartProduct.date}
                chart={chartProduct.chart}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={3}>
              <ReportsLineChart
                color='error'
                title='การลบข้อมูลต่อวัน'
                description={chartDelete.description}
                date={chartDelete.date}
                chart={chartDelete.chart}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderTimeline = () => {
    if (loading) {
      return (
        <MDBox mt={2} mb={2}>
          <Grid container spacing={1}>
            {Array.from(Array(3)).map((_, index) => (
              <Grid item xs={12} md={6} lg={4} key={index.toString()}>
                <Card>
                  <CardHeader
                    avatar={
                      <Skeleton
                        variant='circular'
                        animation='wave'
                        width={40}
                        height={40}
                        sx={{ transform: 'scale(1, 1)' }}
                      />
                    }
                    title={<Skeleton animation='wave' height={10} width='40%' sx={{ mb: 1 }} />}
                    subheader={<Skeleton animation='wave' height={10} width='80%' />}
                  />
                  <CardHeader
                    avatar={
                      <Skeleton
                        variant='circular'
                        animation='wave'
                        width={40}
                        height={40}
                        sx={{ transform: 'scale(1, 1)' }}
                      />
                    }
                    title={<Skeleton animation='wave' height={10} width='40%' sx={{ mb: 1 }} />}
                    subheader={<Skeleton animation='wave' height={10} width='80%' />}
                  />
                  <CardHeader
                    avatar={
                      <Skeleton
                        variant='circular'
                        animation='wave'
                        width={40}
                        height={40}
                        sx={{ transform: 'scale(1, 1)' }}
                      />
                    }
                    title={<Skeleton animation='wave' height={10} width='40%' sx={{ mb: 1 }} />}
                    subheader={<Skeleton animation='wave' height={10} width='80%' />}
                  />
                  <CardHeader
                    avatar={
                      <Skeleton
                        variant='circular'
                        animation='wave'
                        width={40}
                        height={40}
                        sx={{ transform: 'scale(1, 1)' }}
                      />
                    }
                    title={<Skeleton animation='wave' height={10} width='40%' sx={{ mb: 1 }} />}
                    subheader={<Skeleton animation='wave' height={10} width='80%' />}
                  />
                  <CardHeader
                    avatar={
                      <Skeleton
                        variant='circular'
                        animation='wave'
                        width={40}
                        height={40}
                        sx={{ transform: 'scale(1, 1)' }}
                      />
                    }
                    title={<Skeleton animation='wave' height={10} width='40%' sx={{ mb: 1 }} />}
                    subheader={<Skeleton animation='wave' height={10} width='80%' />}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      );
    }

    return (
      <MDBox mt={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <TimelineList title='การทำงานล่าสุด'>
              {timelineLog.map((item) => (
                <TimelineItem
                  key={item.id}
                  color={item.color}
                  icon={item.icon}
                  title={item.title}
                  dateTime={Func.dateTimeDisplay(item.dateTime)}
                  description={`ผู้ใช้งาน : ${item.description}`}
                  badges={item.badges}
                  lastItem={item.lastItem}
                />
              ))}
            </TimelineList>
          </Grid>
          <Grid item xs={12} lg={4}>
            <TimelineList title='การเข้าใช้ Back office ล่าสุด'>
              {timelineUserWeb.map((item) => (
                <TimelineItem
                  key={item.id}
                  color={item.color}
                  icon={item.icon}
                  title={item.title}
                  dateTime={Func.dateTimeDisplay(item.dateTime)}
                  description={`ผู้เข้าใช้ : ${item.description}`}
                  badges={item.badges}
                  lastItem={item.lastItem}
                />
              ))}
            </TimelineList>
          </Grid>
          <Grid item xs={12} lg={4}>
            <TimelineList title='การเข้าใช้ Application ล่าสุด'>
              {timelineUserApp.map((item) => (
                <TimelineItem
                  key={item.id}
                  color={item.color}
                  icon={item.icon}
                  title={item.title}
                  dateTime={Func.dateTimeDisplay(item.dateTime)}
                  description={`ผู้เข้าใช้ : ${item.description}`}
                  badges={item.badges}
                  lastItem={item.lastItem}
                />
              ))}
            </TimelineList>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.dashboard`), t(`menu.dashboard_admin`)]} />
      <MDBox py={3}>
        {renderStat()}
        {renderChart()}
        {renderTimeline()}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardAdmin;
